import {useCallback, useState} from 'react';
import { CGObjectDefinition, CGProductInstance, DataSyncItem, OptimizelyProduct, SyncStrategy } from '../types/DataSyncItem';
import { directoryStore } from '../stores/DirectoryStore';
import { DataSyncMappingService } from '../services/DataSync/DataSyncMappingService';
import { showToasts } from '../lib/toaster';
import { DataSyncViewModel } from '../types/DataSyncViewModel';
import { DataSyncViewModelConverter } from '../utils/dataSyncViewModelConverter';
import { useGetStrategy } from './useDataSyncStrategy';
import { FieldDefinition } from '../types/FieldDefinition';
import DataSyncCgObjectsStore from '../stores/object/DataSyncCgObjectsStore';
import { getApiErrorMessage } from '../lib/ZipApiError';

export function useDataSyncSave(
  initialDataSync: DataSyncItem,
  viewModel: DataSyncViewModel,
  destinationStore: any,
  onSubmit: (syncObj: DataSyncItem) => Promise<void>,
  isValid: () => boolean,
  sourceFields?: FieldDefinition[],
  destinationFields?: FieldDefinition[]
) {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMessage, setLoadingMessage] = useState<string>('');
  const getStrategy = useGetStrategy(viewModel);

  const saveGraphContentType = async (dataSync: DataSyncViewModel) => {
    setLoadingMessage(`${!dataSync.id ? 'Creating' : 'Updating'} Graph Content Type`);
    const transientObjectDefinition = destinationStore.getTransientDestinationObject();
    const dataSyncCgObjectsStore = destinationStore as DataSyncCgObjectsStore;

    const newFields = DataSyncMappingService.filterDisableFields(
      transientObjectDefinition, dataSync.fieldMappings
    );

    dataSyncCgObjectsStore.setTransientDestinationObject({ ...transientObjectDefinition, fields: newFields });
    // filter out disabled mappings before saving
    dataSync.fieldMappings = dataSync.fieldMappings.filter((m) => !m.disabled);
    await dataSyncCgObjectsStore.persistTransientDestinationObject();
    showToasts([
      {
        intent: 'success',
        message: `Graph content type ${
          !dataSync.id ? 'created' : 'updated'
        } successfully`,
      },
    ]);
  };

  const configureCmsIntegration = (
    destinationObjectDefinition: CGObjectDefinition,
    cgInstance: CGProductInstance
  ) => {
    if (!viewModel.destination.config?.content_graph) {
      return;
    }

    if (destinationObjectDefinition?.base_type) {
      viewModel.destination.config.content_graph.base_contract = destinationObjectDefinition.base_type;
    }

    const isCmsIntegrationEnabled = viewModel.destination.config.content_graph?.cms_integration_enabled;
    const cmsInstance = cgInstance?.associated_cms_instance;

    if (isCmsIntegrationEnabled && cmsInstance?.id) {
      viewModel.destination.config.content_graph.cms_instance_id = cmsInstance.id;
    }
  };

  const handleSave = useCallback(async () => {
    if (!isValid()) {
      showToasts([{ intent: 'danger', message: 'Please fix the validation errors before saving.' }]);
      return;
    }
    if (loading) {
      return;
    }

    setLoading(true);

    // make sure that the source app_id is set if the product is HUB_APP
    if (viewModel.source.product === OptimizelyProduct.HUB_APP) {
      viewModel.source.object.app_id = viewModel.source.instance.id;
    }

    const destinationObjectDefinition = destinationStore.getTransientDestinationObject();
    if (viewModel?.destination?.product === OptimizelyProduct.CGR && destinationObjectDefinition) {
      try {
        await saveGraphContentType(viewModel);
      } catch (error: any) {
        setLoading(false);
        const context = `Unable to ${!viewModel.id ? 'create' : 'update'} graph content type`;
        const message = await getApiErrorMessage(error, context);
        showToasts([{ intent: 'danger', message }]);
        return;
      }

      configureCmsIntegration(destinationObjectDefinition, destinationStore.getProductInstance());
    }

    const backendDataSync = DataSyncViewModelConverter.toBackend(
      viewModel,
      undefined,
      sourceFields,
      destinationFields
    );

    const dataSync = {
      ...backendDataSync,
      tracker_id: directoryStore.trackerId,
      interval:
        getStrategy() === SyncStrategy.SCHEDULED
          ? backendDataSync.interval
          : undefined,
    };

    setLoadingMessage('Saving data sync...');
    await onSubmit(dataSync);

    setLoading(false);

    if (viewModel?.destination?.product === OptimizelyProduct.CGR) {
      const isCmsIntegrationEnabled = viewModel.destination.config?.content_graph?.cms_integration_enabled;
      const wasAlreadyEnabled = initialDataSync.destination?.config?.content_graph?.cms_integration_enabled;

      if (isCmsIntegrationEnabled && !wasAlreadyEnabled) {
        showToasts([
          {
            intent: 'success',
            message: 'CMS content source will sync in the background.',
          },
        ]);
      }
    }

  }, [viewModel, destinationStore, onSubmit, isValid, sourceFields, destinationFields]);

  return { handleSave, loading, loadingMessage };
}
